/**
 * 즉시 전송 요청 신호 (TASK-2026-004 REV-003, 중앙 명세 F1-05).
 *
 * ## 문제
 *
 * 주기 전송만으로는 부족한 순간이 있다. 목표를 열었을 때, 시도가 끝났을 때,
 * 화면을 떠날 때다. 이 세 시점의 기록은 그 직후에 참가자가 창을 닫아도 남아야
 * 하는데, 10초 주기만 걸려 있으면 마지막 구간이 통째로 사라진다.
 *
 * ## 왜 모듈 하나를 따로 두는가
 *
 * 기록하는 쪽(`app/telemetry.ts`)과 보내는 쪽(`online/runtime.ts`)은 서로를
 * 모른다. 게임 화면이 전송기를 직접 붙들면 연습 모드에도 전송 경로가 생기고,
 * 전송기가 게임 화면을 알면 import가 순환한다. 그래서 **이름 목록과 신호만
 * 아는 얇은 자리**를 둔다. 듣는 쪽이 없으면 아무 일도 일어나지 않는다 ——
 * 연습 모드에서 원격 요청이 0인 성질은 그대로다(OF1-02).
 */

import type { TelemetryEventName } from '../telemetry/events';

/**
 * 기록되자마자 보내야 하는 이벤트.
 *
 * `result_ready`는 목표를 열어 결과 카드를 조작할 수 있게 된 시점이고,
 * `stage_end`·`stage_retry`는 시도가 끝난 시점이다.
 */
export const FLUSH_ON_RECORD: ReadonlySet<string> = new Set<TelemetryEventName>([
  'result_ready',
  'stage_end',
  'stage_retry',
]);

type FlushListener = () => void;

const listeners = new Set<FlushListener>();

/** 전송기가 듣기 시작한다. 돌려받은 함수를 부르면 그만 듣는다. */
export function onFlushRequested(listener: FlushListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 지금 보내 달라고 알린다. 듣는 쪽이 없으면 아무 일도 없다. */
export function requestFlush(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // 한 전송기의 실패가 다른 전송기나 기록 경로를 멈추게 하지 않는다.
    }
  }
}

/** 방금 기록한 이벤트가 즉시 전송 대상이면 신호를 보낸다. */
export function signalRecorded(name: string): void {
  if (FLUSH_ON_RECORD.has(name)) {
    requestFlush();
  }
}
